import { useEffect, useMemo, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { MESES_ABREV, formatCurrency, formatPercent } from "../../utils/orcamento";
import {
  LinhaRow,
  coletarIdsComFilhos,
  coletarIdsSubgrupo,
  thStyle,
  tdStyle,
  achatarLinhas,
  colunasMensais,
  criarWorkbook,
  adicionarAba,
  baixarWorkbook,
} from "./DreGerencialView";

// Análise por Departamento: tela 100% somente leitura — nenhum valor é editável aqui, tudo vem
// agregado dos módulos de origem (ver backend/services/analise_departamento_svc.py). Realizado e
// Aderência vêm sempre `null` do backend nesta fase (fonte externa ainda não conectada) —
// exibidos literalmente como "-", nunca como R$ 0,00. Trocar o departamento selecionado no
// ranking é uma troca de estado local — os dados de todos os departamentos já vieram numa única
// chamada (`data`), então nunca recarrega o ranking.

const TRIMESTRE_LABELS = ["1º", "2º", "3º", "4º"];

function KpiCard({ label, valor, sublinha, C }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 16px", minWidth: 180, flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{valor}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sublinha}</div>
    </div>
  );
}

function BarraParticipacao({ pct, C }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 140 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 4, background: C.border, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, height: "100%", background: C.brand, borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, minWidth: 42, textAlign: "right" }}>
        {formatPercent(pct)}
      </span>
    </div>
  );
}

function colunasTrimestrais(linha) {
  const colunas = {};
  (linha.trimestres || []).forEach((t, i) => {
    const label = TRIMESTRE_LABELS[i] || `${t.trimestre}º`;
    colunas[`${label} Trim (Plan.)`] = t.planejado;
    colunas[`${label} Trim (Real.)`] = t.realizado === null || t.realizado === undefined ? "-" : t.realizado;
  });
  return colunas;
}

function RankingRow({ dep, ativo, onClick, C }) {
  return (
    <tr
      onClick={onClick}
      style={{
        background: ativo ? C.bgActive : "transparent",
        cursor: "pointer",
        fontWeight: ativo ? 700 : 400,
      }}
    >
      <td style={tdStyle(C, { textAlign: "left" })}>{dep.departamento}</td>
      <td style={tdStyle(C)}>{formatCurrency(dep.total.planejado)}</td>
      <td style={tdStyle(C, { textAlign: "left", minWidth: 160 })}>
        <BarraParticipacao pct={dep.participacao_pct} C={C} />
      </td>
    </tr>
  );
}

export default function AnaliseDepartamentoView({ data, ano, C, departamentoSelecionado, setDepartamentoSelecionado }) {
  const departamentos = useMemo(() => data?.departamentos || [], [data]);
  const deptAtual = useMemo(
    () => departamentos.find((d) => d.departamento === departamentoSelecionado) || departamentos[0] || null,
    [departamentos, departamentoSelecionado]
  );

  const [colapsados, setColapsados] = useState(() => new Set(coletarIdsSubgrupo(deptAtual?.arvore)));
  useEffect(() => {
    setColapsados(new Set(coletarIdsSubgrupo(deptAtual?.arvore)));
  }, [deptAtual]);

  function toggle(id) {
    setColapsados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const arvore = useMemo(() => deptAtual?.arvore || [], [deptAtual]);
  const idsComFilhos = useMemo(() => coletarIdsComFilhos(arvore), [arvore]);

  function exportarParaExcel() {
    const linhasDetalhe = achatarLinhas(arvore).map(({ linha, nivel }) => ({
      "Item": `${"  ".repeat(nivel)}${linha.label}`,
      ...colunasMensais(linha),
      ...colunasTrimestrais(linha),
      "Total Geral (Plan.)": linha.total.planejado,
      "Total Geral (Real.)": linha.total.realizado === null || linha.total.realizado === undefined ? "-" : linha.total.realizado,
      "Aderência (%)": linha.aderencia === null || linha.aderencia === undefined ? "-" : linha.aderencia,
    }));

    const workbook = criarWorkbook();
    adicionarAba(workbook, deptAtual.departamento, linhasDetalhe);
    baixarWorkbook(workbook, `Analise_Departamento_${deptAtual.departamento}_${ano}`);
  }

  if (!deptAtual) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* KPIs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <KpiCard label="Custo Total do Grupo (Ano)" valor={formatCurrency(data.custo_total_grupo.planejado)} sublinha="Realizado: -" C={C} />
        <KpiCard label={`Custo · ${deptAtual.departamento}`} valor={formatCurrency(deptAtual.total.planejado)} sublinha="Realizado: -" C={C} />
        <KpiCard label="% Participação no Grupo" valor={formatPercent(deptAtual.participacao_pct)} sublinha={deptAtual.departamento} C={C} />
        <KpiCard
          label="Maior Categoria de Custo"
          valor={deptAtual.maior_categoria ? deptAtual.maior_categoria.label : "-"}
          sublinha={deptAtual.maior_categoria ? formatCurrency(deptAtual.maior_categoria.planejado) : "-"}
          C={C}
        />
      </div>

      {/* Ranking de departamentos */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle(C, { textAlign: "left" })}>Departamento</th>
                <th style={thStyle(C)}>Planejado</th>
                <th style={thStyle(C, { textAlign: "left" })}>Participação</th>
              </tr>
            </thead>
            <tbody>
              {departamentos.map((dep) => (
                <RankingRow
                  key={dep.departamento}
                  dep={dep}
                  ativo={dep.departamento === deptAtual.departamento}
                  onClick={() => setDepartamentoSelecionado(dep.departamento)}
                  C={C}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detalhamento do departamento selecionado */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button
          type="button"
          onClick={exportarParaExcel}
          disabled={departamentos.length === 0}
          title="Exportar dados para Excel"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "transparent", border: `1px solid ${C.success}`, borderRadius: 8,
            padding: "6px 12px", fontSize: 11.5, fontWeight: 600, color: C.success,
            cursor: departamentos.length === 0 ? "not-allowed" : "pointer", opacity: departamentos.length === 0 ? 0.45 : 1,
          }}
        >
          <FileSpreadsheet size={14} /> Exportar Excel
        </button>
        <button
          type="button"
          onClick={() => setColapsados(colapsados.size > 0 ? new Set() : new Set(idsComFilhos))}
          style={{
            background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "6px 12px", fontSize: 11.5, fontWeight: 600, color: C.text, cursor: "pointer",
          }}
        >
          {colapsados.size > 0 ? "Expandir tudo" : "Recolher tudo"}
        </button>
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1400 }}>
            <thead>
              <tr>
                <th style={thStyle(C, { textAlign: "left" })}>{deptAtual.departamento} · {ano}</th>
                {MESES_ABREV.map((m) => (
                  <th key={m} style={thStyle(C, { textAlign: "center" })}>{m}</th>
                ))}
                {TRIMESTRE_LABELS.map((t, i) => (
                  <th
                    key={t}
                    style={thStyle(C, {
                      textAlign: "center", background: C.bgActive,
                      borderLeft: i === 0 ? `2px solid ${C.brand}` : undefined,
                    })}
                  >
                    {t} Trim
                  </th>
                ))}
                <th style={thStyle(C, { textAlign: "center", background: C.bgActive, fontWeight: 800 })}>Total Geral</th>
                <th style={thStyle(C)}>Aderência</th>
              </tr>
            </thead>
            <tbody>
              {arvore.length === 0 ? (
                <tr>
                  <td colSpan={1 + 12 + 4 + 1 + 1} style={tdStyle(C, { textAlign: "left", color: C.muted })}>
                    Nenhum lançamento para este departamento em {ano}.
                  </td>
                </tr>
              ) : (
                arvore.map((linha) => (
                  <LinhaRow key={linha.id} linha={linha} nivel={0} colapsados={colapsados} toggle={toggle} C={C} ano={ano} comTrimestres />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
